import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  BorderColorOutlined,
  DeleteOutline,
  Save,
  Cancel,
  VpnKeyOutlined,
} from "@mui/icons-material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridSlotProps,
  GridToolbarContainer,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { cloneDeep } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { AdminData } from "@/interfaces";
import { baseUrl, settingId } from "@/consts";
import { useIsAdminMode, useSettings } from "@/contexts";
import { useDataApi } from "@/hooks";
import { MuiHelper, FormikHelper, SettingsHelper } from "@/helpers";
import {
  formatAxiosError,
  getInt,
  isNonEmptyArray,
  isNonEmptyString,
  isStringCommaSeparatedIntegers,
  reFloat,
  reInt,
  reMoney,
} from "@/utils";
import { colors, sxButton, sxTextField } from "@/theme";
import { ErrorMessage } from "@/components/atoms";
import { sxSettingTextField } from "@/theme";

export interface SettingsEditFormProps { }

export interface SettingsEditFormRef {
  saveChanges: () => void;
}

const validationSchema = Yup.object().shape({
  scannerInputDebounceWait: Yup.number()
    .integer()
    .required('"Scanner Input Debounce Wait" is required'),
  productBarCodeLength: Yup.string()
    .test(
      "is-comma-separated-integers",
      'Must be integers separated by commas (e.g., "1, 2, 3")',
      isStringCommaSeparatedIntegers
    )
    .required('"Product Bar Code Length" is required'),
  defaultProductImageUrl: Yup.string().required(
    '"Default Product Image URL" is required'
  ),
  coopEligible: Yup.number().required('"Co-Op Eligible" is required'),
  creditCardNumberLength: Yup.string()
    .test(
      "is-comma-separated-integers",
      'Must be integers separated by commas (e.g., "1, 2, 3")',
      isStringCommaSeparatedIntegers
    )
    .required('"Credit Card Number Length" is required'),
  shippingFee: Yup.number().required('"Shipping Fee" is required'),
  claimFee: Yup.number().required('"Claim Fee" is required'),
  fedExEmail: Yup.string().required('"FedEx Email" is required'),
  packingSlipEmail: Yup.string().required('"Packing Slip Email" is required'),
  orderEmailBcc: Yup.string().required('"Order Email Bcc" is required'),
  dealerBadgeCodeLength: Yup.string()
    .test(
      "is-comma-separated-integers",
      'Must be integers separated by commas (e.g., "1, 2, 3")',
      isStringCommaSeparatedIntegers
    )
    .required('"Dealer Badge Code Length" is required'),
  couponAmount: Yup.number().required('"Coupon Amount" is required'),
});

const sxContainer = {
  padding: "10px",
};

const sxInputsContainer = {
  display: "grid",
  gap: "10px 10px",
  padding: "20px 0",
};

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel
    ) => void;
  }
}

const newRowId = -1;

const mapAdminsToRows = (admins?: AdminData[]): readonly GridValidRowModel[] =>
  (admins ?? []).map((item: AdminData, index: number) => ({
    ...item,
    id: index,
    hasProductsPermission: SettingsHelper.hasProductsPermission(
      item.permissions
    ),
    hasOrdersPermission: SettingsHelper.hasOrdersPermission(item.permissions),
    hasShippingPortalPermission:
      SettingsHelper.hasShippingPortalPermission(item.permissions),
    hasAttendeesPermission: SettingsHelper.hasAttendeesPermission(
      item.permissions
    ),
    hasReportingPermission: SettingsHelper.hasReportingPermission(
      item.permissions
    ),
    hasInventoryCountsPermission:
      SettingsHelper.hasInventoryCountsPermission(item.permissions),
    hasSettingsPermission: SettingsHelper.hasSettingsPermission(
      item.permissions
    ),
  }));

function EditToolbar(props: GridSlotProps["toolbar"]) {
  const { setRows, setRowModesModel } = props;

  const handleClick = () => {
    const id = newRowId;
    setRows((oldRows) => [...oldRows, { id, email: "", isNew: true }]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "email" },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button onClick={handleClick} sx={{
        color: colors.white,
        background: colors.primaryOzone500,
        borderRadius: "10px",
        padding: "8px 18px",
        fontFamily:'PF DIN Text Pro',
        fontSize:18,
        fontWeight:500,
        textTransform:'none',
        '&:hover': {
          background: colors.ozone,
        },
      }}>
        Add Admin
      </Button>
    </GridToolbarContainer>
  );
}

interface ResetPasswordDialogProps {
  open: boolean;
  email?: string;
  onClose: () => void;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  email,
  onClose,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState({
    newPassword: false,
    confirmNewPassword: false,
  });

  useEffect(() => {
    if (open) {
      setNewPassword("");
      setConfirmNewPassword("");
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      setFieldErrors({
        newPassword: false,
        confirmNewPassword: false,
      });
    }
  }, [open, email]);

  const handleClose = () => {
    onClose();
  };

  const handleResetPassword = async () => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setFieldErrors({
      newPassword: false,
      confirmNewPassword: false,
    });

    const errors = {
      newPassword: !newPassword,
      confirmNewPassword: !confirmNewPassword,
    };

    if (newPassword && confirmNewPassword && newPassword !== confirmNewPassword) {
      setErrorMessage("New passwords do not match");
      setFieldErrors({
        ...errors,
        newPassword: true,
        confirmNewPassword: true,
      });
      return;
    }

    if (Object.values(errors).some((error) => error)) {
      setErrorMessage("Both password fields are required");
      setFieldErrors(errors);
      return;
    }

    if (!email) {
      setErrorMessage("User email is not available");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${baseUrl.SETTING}/reset-password`, {
        userEmail: email,
        newPassword,
      });

      if (response?.data?.success) {
        setSuccessMessage(response.data.message || "Password changed successfully");
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to change password. Please try again."
        );
      }
    } catch (error: any) {
      setErrorMessage(formatAxiosError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "PF DIN Text Pro",
              fontSize: 18,
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            RESET PASSWORD
          </Typography>
          <TextField
            value={email || ""}
            id="resetPasswordEmail"
            label="Email"
            type="email"
            fullWidth
            disabled
            sx={sxTextField}
          />
          <TextField
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrorMessage(undefined);
              setFieldErrors((prev) => ({ ...prev, newPassword: false }));
            }}
            value={newPassword}
            id="resetNewPassword"
            label="New Password"
            type="password"
            fullWidth
            error={fieldErrors.newPassword}
            helperText={
              fieldErrors.newPassword ? "New password is required" : undefined
            }
            placeholder="Enter new password"
            sx={sxTextField}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => {
              setConfirmNewPassword(e.target.value);
              setErrorMessage(undefined);
              setFieldErrors((prev) => ({ ...prev, confirmNewPassword: false }));
            }}
            value={confirmNewPassword}
            id="resetConfirmNewPassword"
            label="Confirm New Password"
            type="password"
            fullWidth
            error={fieldErrors.confirmNewPassword}
            helperText={
              fieldErrors.confirmNewPassword
                ? "Please confirm the new password"
                : undefined
            }
            placeholder="Confirm new password"
            sx={sxTextField}
            disabled={isLoading}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isLoading) {
                handleResetPassword();
              }
            }}
          />
          {errorMessage && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              sx={{
                color: "#4caf50",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {successMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          p: 3,
        }}
      >
        <Button
          onClick={handleResetPassword}
          disabled={isLoading}
          sx={{
            width: "100%",
            color: colors.white,
            background: colors.ozone,
            borderRadius: "12px",
            p: "12px",
            fontFamily: "PF DIN Text Pro",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "16px",
            textTransform: "uppercase",
            "&:hover": {
              color: colors.white,
              background: colors.ozone,
            },
            "&.Mui-disabled": {
              color: colors.white,
              background: colors.neutralGrey500,
            },
          }}
        >
          {isLoading ? "RESETTING..." : "RESET PASSWORD"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteAccountDialogProps {
  open: boolean;
  email?: string;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  email,
  onClose,
  onDeleted,
}) => {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  const normalizedEmail = email?.trim().toLowerCase() ?? "";
  const normalizedConfirm = confirmEmail.trim().toLowerCase();
  const isMatch = normalizedEmail.length > 0 && normalizedEmail === normalizedConfirm;

  useEffect(() => {
    if (open) {
      setConfirmEmail("");
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
    }
  }, [open, email]);

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleDelete = async () => {
    if (!email || !isMatch) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);

    try {
      const response = await axios.post(`${baseUrl.SETTING}/delete-account`, {
        userEmail: email,
      });

      if (response?.data?.success) {
        setSuccessMessage(response.data.message || "Account deleted successfully");
        onDeleted();
        setTimeout(() => {
          onClose();
        }, 800);
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to delete account. Please try again."
        );
      }
    } catch (error: any) {
      setErrorMessage(formatAxiosError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "PF DIN Text Pro",
              fontSize: 18,
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            DELETE ACCOUNT
          </Typography>
          <Typography
            sx={{
              fontFamily: "PF DIN Text Pro",
              fontSize: 14,
              textAlign: "center",
              mb: 1,
            }}
          >
            Type the email of the account to confirm deletion.
          </Typography>
          <TextField
            value={email || ""}
            id="deleteAccountEmail"
            label="Email"
            type="email"
            fullWidth
            disabled
            sx={sxTextField}
          />
          <TextField
            onChange={(e) => {
              setConfirmEmail(e.target.value);
              setErrorMessage(undefined);
            }}
            value={confirmEmail}
            id="deleteAccountConfirmEmail"
            label="Confirm Email"
            type="email"
            fullWidth
            placeholder="Type the email to confirm"
            sx={sxTextField}
            disabled={isLoading}
          />
          {!isMatch && normalizedConfirm.length > 0 && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "13px",
                mt: -1,
                textAlign: "left",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              Entered email does not match.
            </Typography>
          )}
          {errorMessage && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              sx={{
                color: "#4caf50",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {successMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          p: 3,
        }}
      >
        <Button
          onClick={handleDelete}
          disabled={isLoading || !isMatch}
          sx={{
            width: "100%",
            color: colors.white,
            background: "#d32f2f",
            borderRadius: "12px",
            p: "12px",
            fontFamily: "PF DIN Text Pro",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "16px",
            textTransform: "uppercase",
            "&:hover": {
              color: colors.white,
              background: "#b71c1c",
            },
            "&.Mui-disabled": {
              color: colors.white,
              background: colors.neutralGrey500,
            },
          }}
        >
          {isLoading ? "DELETING..." : "DELETE ACCOUNT"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CreateAccountDialogProps {
  open: boolean;
  email?: string;
  permission?: number;
  onClose: () => void;
  onCreated: () => void;
}

const CreateAccountDialog: React.FC<CreateAccountDialogProps> = ({
  open,
  email,
  permission,
  onClose,
  onCreated,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirmPassword("");
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
    }
  }, [open, email, permission]);

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleCreate = async () => {
    if (!email || typeof permission !== "number") {
      setErrorMessage("Email or permissions are missing.");
      return;
    }
    if (!password || !confirmPassword) {
      setErrorMessage("Both password fields are required.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);

    try {
      const response = await axios.post(`${baseUrl.SETTING}/create-account`, {
        userEmail: email,
        password,
        permission,
      });

      if (response?.data?.success) {
        setSuccessMessage(
          response.data.message || "User account created successfully"
        );
        onCreated();
        setTimeout(() => {
          onClose();
        }, 800);
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to create account. Please try again."
        );
      }
    } catch (err: any) {
      setErrorMessage(formatAxiosError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "PF DIN Text Pro",
              fontSize: 18,
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            CREATE ACCOUNT
          </Typography>
          <TextField
            value={email || ""}
            id="createAccountEmail"
            label="Email"
            type="email"
            fullWidth
            disabled
            sx={sxTextField}
          />
          <TextField
            onChange={(e) => {
              setPassword(e.target.value);
              setErrorMessage(undefined);
            }}
            value={password}
            id="createAccountPassword"
            label="Password"
            type="password"
            fullWidth
            placeholder="Enter password"
            sx={sxTextField}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrorMessage(undefined);
            }}
            value={confirmPassword}
            id="createAccountConfirmPassword"
            label="Confirm Password"
            type="password"
            fullWidth
            placeholder="Confirm password"
            sx={sxTextField}
            disabled={isLoading}
          />
          {errorMessage && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              sx={{
                color: "#4caf50",
                fontSize: "14px",
                mt: 1,
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {successMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          p: 3,
        }}
      >
        <Button
          onClick={handleCreate}
          disabled={isLoading}
          sx={{
            width: "100%",
            color: colors.white,
            background: colors.ozone,
            borderRadius: "12px",
            p: "12px",
            fontFamily: "PF DIN Text Pro",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "16px",
            textTransform: "uppercase",
            "&:hover": {
              color: colors.white,
              background: colors.ozone,
            },
            "&.Mui-disabled": {
              color: colors.white,
              background: colors.neutralGrey500,
            },
          }}
        >
          {isLoading ? "CREATING..." : "CREATE ACCOUNT"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SettingsEditForm = forwardRef<SettingsEditFormRef, SettingsEditFormProps>(
  (_, ref) => {
    const { settings, setSettings } = useSettings();
    const { currentAdminData } = useIsAdminMode();
    const { put, error } = useDataApi();
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined
    );

    const { errors, touched, handleBlur, ...formik } = useFormik({
      enableReinitialize: true,
      validationSchema: validationSchema,
      initialValues: cloneDeep(SettingsHelper.toDto(settings, true)),
      onSubmit(values, { validateForm }) {
        validateForm(values).then(() => console.log(formik.values));
      },
    });

    const [rows, setRows] = useState<readonly GridValidRowModel[]>([]);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] =
      useState(false);
    const [resetPasswordEmail, setResetPasswordEmail] = useState<
      string | undefined
    >();
    const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] =
      useState(false);
    const [deleteAccountEmail, setDeleteAccountEmail] = useState<
      string | undefined
    >();
    const [deleteAccountRowId, setDeleteAccountRowId] =
      useState<GridRowId | null>(null);
    const [isCreateAccountDialogOpen, setIsCreateAccountDialogOpen] =
      useState(false);
    const [createAccountEmail, setCreateAccountEmail] = useState<
      string | undefined
    >();
    const [createAccountPermission, setCreateAccountPermission] = useState<
      number | undefined
    >();

    const canResetPasswords = SettingsHelper.hasSettingsPermission(
      currentAdminData?.permissions
    );

    useEffect(() => {
      const currentRows = [...(rows as GridRowsProp)];

      if (!canResetPasswords || isNonEmptyArray(currentRows)) {
        return;
      }

      axios
        .get(`${baseUrl.SETTING}/admins`)
        .then((response) => {
          const admins = SettingsHelper.toAdminData(response?.data?.admins);
          if (admins) {
            formik.setFieldValue("admins", admins);
            setRows(mapAdminsToRows(admins));
          }
        })
        .catch((err) => {
          setErrorMessage(formatAxiosError(err));
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canResetPasswords, rows]);

    useEffect(() => {
      if (
        error?.config?.url?.indexOf(baseUrl.SETTING) >= 0 &&
        !isNonEmptyString(error)
      ) {
        setErrorMessage(formatAxiosError(error));
      }
    }, [error]);

    const saveChanges = async () => {
      if (!settings || !formik.dirty) {
        return;
      }

      setErrorMessage(undefined);

      try {
        validationSchema.validateSync(formik.values);

        const newSettings = SettingsHelper.fromDto(formik.values, true);
        setSettings(newSettings);

        await put?.(
          `${baseUrl.SETTING}/${settingId}`,
          SettingsHelper.toDto(newSettings)
        );
      } catch (e) {
        if (e instanceof Yup.ValidationError) {
          if (isNonEmptyArray(e.errors)) {
            setErrorMessage(e.errors[0]);
          } else {
            setErrorMessage("Unknown validation error");
            console.log(e);
          }
        } else {
          console.log(e);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      saveChanges: saveChanges,
    }));

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (
      params,
      event
    ) => {
      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
        event.defaultMuiPrevented = true;
      }
    };

    const handleEditClick = (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: GridRowId) => () => {
      const row = rows.find((row) => row.id === id) as GridValidRowModel | undefined;
      const email = (row as any)?.email as string | undefined;

      if (email) {
        setDeleteAccountRowId(id);
        setDeleteAccountEmail(email);
        setIsDeleteAccountDialogOpen(true);
      }
    };

    const handleCancelClick = (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });

      const editedRow = rows?.find((row) => row.id === id);
      if (editedRow!.isNew) {
        setRows(rows?.filter((row) => row.id !== id));
      }
    };

    const processRowUpdate = async (newRow: GridRowModel) => {
      const oldRow = rows.find(
        (row) => row.id === newRow.id
      ) as GridValidRowModel | undefined;
      const oldPermissions = (oldRow as any)?.permissions as number | undefined;
      const wasNewRow = (oldRow as any)?.isNew === true;

      newRow.permissions = SettingsHelper.setPermissions(
        newRow.hasProductsPermission,
        newRow.hasOrdersPermission,
        newRow.hasShippingPortalPermission,
        newRow.hasAttendeesPermission,
        newRow.hasReportingPermission,
        newRow.hasInventoryCountsPermission,
        newRow.hasSettingsPermission
      );

      const updatedRow = { ...newRow, isNew: false };
      const newRows = rows?.map((row) =>
        row.id === newRow.id ? updatedRow : row
      );

      if (newRow.id === newRowId) {
        const newId = isNonEmptyArray(newRows)
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Math.max(
              ...(newRows.map((item) => (item as any).id) as number[])
            ) + 1
          : 0;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newRowWithTempId = newRows.find(
          (item) => (item as any).id === newRowId
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (newRowWithTempId) (newRowWithTempId as any).id = newId;
      }

      setRows(newRows);
      formik.setFieldValue("admins", SettingsHelper.toAdminData(newRows));

      const email = (updatedRow as any)?.email as string | undefined;
      const newPermissions = (updatedRow as any)?.permissions as number | undefined;
      const isExistingRow = !wasNewRow;

      // Existing admin: update permissions
      if (
        email &&
        typeof newPermissions === "number" &&
        newPermissions !== oldPermissions &&
        isExistingRow
      ) {
        try {
          await axios.post(`${baseUrl.SETTING}/edit-permissions`, {
            userEmail: email,
            newPermission: newPermissions,
          });

          // Inform the admin that the user may need to re-login
          // for permission changes to take effect immediately.
          // eslint-disable-next-line no-alert
          alert(
            "Permissions updated. The user may need to log out and back in for changes to take effect immediately."
          );
        } catch (err: any) {
          setErrorMessage(formatAxiosError?.(err) ?? "Failed to update permissions");
        }
      }

      // New admin row: open create-account dialog so password can be set
      if (
        email &&
        typeof newPermissions === "number" &&
        wasNewRow &&
        !isExistingRow
      ) {
        setCreateAccountEmail(email);
        setCreateAccountPermission(newPermissions);
        setIsCreateAccountDialogOpen(true);
      }

      return updatedRow;
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
      setRowModesModel(newRowModesModel);
    };

    const handleResetPasswordClick = (id: GridRowId) => () => {
      const row = rows.find((row) => row.id === id) as GridValidRowModel | undefined;
      const email = (row as any)?.email as string | undefined;

      if (email) {
        setResetPasswordEmail(email);
        setIsResetPasswordDialogOpen(true);
      }
    };

    const handleAccountDeleted = () => {
      if (deleteAccountRowId === null) return;

      const newRows = rows?.filter((row) => row.id !== deleteAccountRowId);
      setRows(newRows);
      formik.setFieldValue("admins", SettingsHelper.toAdminData(newRows));
      setDeleteAccountRowId(null);
      setDeleteAccountEmail(undefined);
    };

    const columns: GridColDef[] = [
      { field: "email", headerName: "Email", editable: true, flex: 1 },
      {
        field: "hasProductsPermission",
        type: "boolean",
        headerName: "Products",
        editable: true,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasOrdersPermission",
        type: "boolean",
        headerName: "Orders",
        editable: true,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasShippingPortalPermission",
        type: "boolean",
        headerName: "Shipping portal",
        editable: true,
        width: 150,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasAttendeesPermission",
        type: "boolean",
        headerName: "Attendees",
        editable: true,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasReportingPermission",
        type: "boolean",
        headerName: "Reporting",
        editable: true,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasInventoryCountsPermission",
        type: "boolean",
        headerName: "Inventory Counts",
        editable: true,
        width: 150,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "hasSettingsPermission",
        type: "boolean",
        headerName: "Settings",
        editable: true,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 140,
        cellClassName: "actions",
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<Save />}
                label="Save"
                sx={{
                  color: "primary.main",
                }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                icon={<Cancel />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          const actions = [
            <GridActionsCellItem
              icon={<BorderColorOutlined />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteOutline />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];

          if (canResetPasswords) {
            actions.push(
              <GridActionsCellItem
                icon={<VpnKeyOutlined />}
                label="Reset Password"
                onClick={handleResetPasswordClick(id)}
                color="inherit"
              />
            );
          }

          return actions;
        },
      },
    ];

    return (
      <Box sx={{ padding: 3 }}>
        <Box sx={{ backgroundColor: colors.white, p: 2, borderRadius: 3 }}>
          <Box sx={sxContainer}>
            {MuiHelper.getHeader("Product Settings")}
            <Divider sx={{ mt: 2 }} />
            <Box
              sx={{
                ...sxInputsContainer,
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                '@media (max-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
                '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
                gap: '24px',
              }}
            >
              <Box>
                <Typography sx={sxSettingTextField}>SCANNER INPUT</Typography>
                <TextField
                  id="scannerInputDebounceWait"
                  name="scannerInputDebounceWait"
                  required
                  value={formik.values.scannerInputDebounceWait}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "scannerInputDebounceWait",
                      formik.setFieldValue,
                      getInt,
                      reInt
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.scannerInputDebounceWait) &&
                    touched.scannerInputDebounceWait
                  }
                  helperText={errors.scannerInputDebounceWait}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>PRODUCT BARCODE</Typography>
                <TextField
                  id="productBarCodeLength"
                  name="productBarCodeLength"
                  required
                  value={formik.values.productBarCodeLength}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("productBarCodeLength", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.productBarCodeLength) &&
                    touched.productBarCodeLength
                  }
                  helperText={errors.productBarCodeLength}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>DEFAULT PRODUCT</Typography>
                <TextField
                  id="defaultProductImageUrl"
                  name="defaultProductImageUrl"
                  required
                  value={formik.values.defaultProductImageUrl}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("defaultProductImageUrl", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.defaultProductImageUrl) &&
                    touched.defaultProductImageUrl
                  }
                  helperText={errors.defaultProductImageUrl}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>CO-OP ELIGIBLE</Typography>
                <TextField
                  id="coopEligible"
                  name="coopEligible"
                  required
                  value={formik.values.coopEligible}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "coopEligible",
                      formik.setFieldValue,
                      undefined,
                      reFloat
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.coopEligible) && touched.coopEligible
                  }
                  helperText={errors.coopEligible}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={sxContainer}>
            {MuiHelper.getHeader("Order Settings")}
            <Divider sx={{ mt: 2 }} />
            <Box
              sx={{
                ...sxInputsContainer,
                gridTemplateColumns: "1fr 1fr 1fr",
                '@media (max-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
                '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
                gap: '24px',
              }}
            >
              <Box>
                <Typography sx={sxSettingTextField}>CREDIT CARD NUMBER LENGTH</Typography>
                <TextField
                  id="creditCardNumberLength"
                  name="creditCardNumberLength"
                  required
                  value={formik.values.creditCardNumberLength}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("creditCardNumberLength", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.creditCardNumberLength) &&
                    touched.creditCardNumberLength
                  }
                  helperText={errors.creditCardNumberLength}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>SHIPPING FEE</Typography>
                <TextField
                  id="shippingFee"
                  name="shippingFee"
                  required
                  value={formik.values.shippingFee}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "shippingFee",
                      formik.setFieldValue,
                      undefined,
                      reMoney
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.shippingFee) && touched.shippingFee
                  }
                  helperText={errors.shippingFee}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>CLAIM FEE</Typography>
                <TextField
                  id="claimFee"
                  name="claimFee"
                  required
                  value={formik.values.claimFee}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "claimFee",
                      formik.setFieldValue,
                      undefined,
                      reMoney
                    )
                  }
                  onBlur={handleBlur}
                  error={isNonEmptyString(errors.claimFee) && touched.claimFee}
                  helperText={errors.claimFee}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>FEDEX EMAIL</Typography>
                <TextField
                  id="fedExEmail"
                  name="fedExEmail"
                  required
                  value={formik.values.fedExEmail}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("fedExEmail", value)
                  }
                  onBlur={handleBlur}
                  error={isNonEmptyString(errors.fedExEmail) && touched.fedExEmail}
                  helperText={errors.fedExEmail}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>PACKING SLIP EMAIL</Typography>
                <TextField
                  id="packingSlipEmail"
                  name="packingSlipEmail"
                  required
                  value={formik.values.packingSlipEmail}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("packingSlipEmail", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.packingSlipEmail) &&
                    touched.packingSlipEmail
                  }
                  helperText={errors.packingSlipEmail}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>ORDER EMAIL BCC</Typography>
                <TextField
                  id="orderEmailBcc"
                  name="orderEmailBcc"
                  required
                  value={formik.values.orderEmailBcc}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("orderEmailBcc", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.orderEmailBcc) && touched.orderEmailBcc
                  }
                  helperText={errors.orderEmailBcc}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={sxContainer}>
            {MuiHelper.getHeader("Dealer Settings")}
            <Divider sx={{ mt: 2 }} />
            <Box sx={{ ...sxInputsContainer, gridTemplateColumns: "1fr 1fr", gap: '24px', '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } }}>
              <Box>
                <Typography sx={sxSettingTextField}>DEALER BADGE CODE LENGTH</Typography>
                <TextField
                  id="dealerBadgeCodeLength"
                  name="dealerBadgeCodeLength"
                  required
                  value={formik.values.dealerBadgeCodeLength}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("dealerBadgeCodeLength", value)
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.dealerBadgeCodeLength) &&
                    touched.dealerBadgeCodeLength
                  }
                  helperText={errors.dealerBadgeCodeLength}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={sxSettingTextField}>COUPON AMOUNT</Typography>
                <TextField
                  id="couponAmount"
                  name="couponAmount"
                  required
                  value={formik.values.couponAmount}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "couponAmount",
                      formik.setFieldValue,
                      undefined,
                      reMoney
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.couponAmount) && touched.couponAmount
                  }
                  helperText={errors.couponAmount}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: {
                      borderRadius: '12px',
                      background: '#fff',
                      fontSize: 16,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                    },
                  }}
                  sx={{ ...sxTextField, mb: 0 }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={sxContainer}>
            {MuiHelper.getHeader("Admins")}
            {/* <Divider /> */}
            <DataGrid
              sx={{ borderRadius: 2, p: 2 }}
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              slots={{ toolbar: EditToolbar }}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
              }}
              hideFooter={true}
              hideFooterPagination={true}
            />
          </Box>
        </Box>
        <ResetPasswordDialog
          open={isResetPasswordDialogOpen}
          email={resetPasswordEmail}
          onClose={() => setIsResetPasswordDialogOpen(false)}
        />
        <DeleteAccountDialog
          open={isDeleteAccountDialogOpen}
          email={deleteAccountEmail}
          onClose={() => setIsDeleteAccountDialogOpen(false)}
          onDeleted={handleAccountDeleted}
        />
        <CreateAccountDialog
          open={isCreateAccountDialogOpen}
          email={createAccountEmail}
          permission={createAccountPermission}
          onClose={() => setIsCreateAccountDialogOpen(false)}
          onCreated={() => {
            // eslint-disable-next-line no-alert
            alert(
              "User account created successfully. The user can now log in with the specified password."
            );
          }}
        />
        <ResetPasswordDialog
          open={isResetPasswordDialogOpen}
          email={resetPasswordEmail}
          onClose={() => setIsResetPasswordDialogOpen(false)}
        />
        {!!errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </Box>
    );
  }
);

export default SettingsEditForm;
